

// /frontend/dashboard/src/router/modules/dashboardRoutes.js
// Dashboard route module for realtime fleet monitoring pages.
// English explanations are followed by Thai explanations for route-design validation and debugging.

import { debugLog } from '../../utils/debugGate.js';


const ROUTE_DEBUG_NAMESPACE = '[router/dashboardRoutes]';

function emitDashboardRouteDebug(label, payload) {
  debugLog(label, payload);
}

// Static import registry — Vite can statically analyze these at build time.
// English: All component imports must be literal string paths inside this registry.
//          Never use runtime variable strings inside import() — Vite cannot bundle them.
// ไทย: import ทุกตัวต้องเป็น literal string path ใน registry นี้
//      ห้ามใช้ variable string ใน import() เพราะ Vite จะไม่สร้าง chunk สำหรับ production
const PAGE_REGISTRY = {
  DashboardPage: () => import('../../pages/DashboardPage.vue'),
  DashboardPageTest: () => import('../../pages/DashboardPageTest.vue'),
  // English: FleetDashboardPage is the main robot operations page — live map, robot status, missions, alerts.
  // ไทย: FleetDashboardPage คือหน้า operations หลัก — map สด, สถานะหุ่นยนต์, missions, alerts
  FleetDashboardPage: () => import('../../pages/FleetDashboardPage.vue'),
  // English: RobotChannelExplorerPage is the per-channel debug/monitoring page — view telemetry/status/mission/alert/ack/command/map/factsheet data per robot.
  // ไทย: RobotChannelExplorerPage คือหน้า debug/monitoring แยกตาม channel — ดูข้อมูล telemetry/status/mission/alert/ack/command/map/factsheet ต่อ robot
  RobotChannelExplorerPage: () => import('../../pages/RobotChannelExplorerPage.vue'),
  // English: MobileCommandPage is the mobile-first command interface — E-STOP, motion, mission, ACK display.
  // ไทย: MobileCommandPage คือหน้า command แบบ mobile-first — E-STOP, motion, mission, แสดง ACK
  MobileCommandPage: () => import('../../pages/MobileCommandPage.vue'),
  // English: MapBuilderPage is the SLAM map viewer and zone builder — scan area, draw zone polygon, generate waypoints, create mission.
  // ไทย: MapBuilderPage คือหน้าดูแผนที่ SLAM และวาด zone — สแกนพื้นที่, วาด zone polygon, สร้าง waypoints, สร้าง mission
  MapBuilderPage: () => import('../../pages/MapBuilderPage.vue'),
  // English: RobotThresholdsPage is the operator settings page — configure per-robot thresholds
  //          (battery return %, min-to-start %, critical %, scrub clean/dirty abort %) and push to robot.
  // ไทย: RobotThresholdsPage คือหน้าตั้งค่าสำหรับ operator — ตั้ง threshold ต่อ robot
  //      (แบต return %, min-to-start %, critical %, scrub clean/dirty abort %) และ push ไปหุ่นยนต์
  RobotThresholdsPage: () => import('../../pages/RobotThresholdsPage.vue'),
  // English: RobotCalibrationPage is the mechanical calibration page — configure Ackermann
  //          steering params (wheelbase, max angle, PID Kp) and monitor live steering error.
  // ไทย: RobotCalibrationPage คือหน้า calibrate ทางกล — ตั้ง Ackermann steering params
  //      (wheelbase, มุมสูงสุด, PID Kp) และดู steering error แบบ live
  RobotCalibrationPage: () => import('../../pages/RobotCalibrationPage.vue'),
  // English: RobotBladeCalibrationPage — blade motor calibration for MOWING robots (target RPM, overcurrent, start delay).
  // ไทย: RobotBladeCalibrationPage — calibrate blade motor สำหรับ MOWING robot (target RPM, overcurrent, delay ก่อนเริ่ม)
  RobotBladeCalibrationPage: () => import('../../pages/RobotBladeCalibrationPage.vue'),
  // English: RobotNavCalibrationPage — navigation speed profile and obstacle detection for all robots.
  // ไทย: RobotNavCalibrationPage — ตั้ง speed profile และระยะ obstacle detection สำหรับ robot ทุกชนิด
  RobotNavCalibrationPage: () => import('../../pages/RobotNavCalibrationPage.vue'),
  // English: RobotScrubCalibrationPage — pump flow and brush RPM calibration for SCRUBBING robots.
  // ไทย: RobotScrubCalibrationPage — calibrate pump flow และ brush RPM สำหรับ SCRUBBING robot
  RobotScrubCalibrationPage: () => import('../../pages/RobotScrubCalibrationPage.vue'),

  // English: FleetAnalyticsPage — fleet KPIs: utilization, coverage/day, MTBF, battery health, per-robot breakdown.
  // ไทย: FleetAnalyticsPage — KPI fleet: utilization, coverage/วัน, MTBF, battery health, แยกตาม robot
  FleetAnalyticsPage: () => import('../../pages/FleetAnalyticsPage.vue'),

  // English: ScheduledMissionsPage — CRUD for recurring mission schedules (cron or interval).
  // ไทย: ScheduledMissionsPage — CRUD สำหรับ scheduled mission (cron หรือ interval ซ้ำ)
  ScheduledMissionsPage: () => import('../../pages/ScheduledMissionsPage.vue'),

  // English: RobotMaintenanceLogPage — per-robot service history, log new maintenance events.
  // ไทย: RobotMaintenanceLogPage — ประวัติการซ่อมบำรุงต่อ robot บันทึก service event ใหม่
  RobotMaintenanceLogPage: () => import('../../pages/RobotMaintenanceLogPage.vue'),

  // English: NotificationInboxPage — persistent in-app notification inbox with read/dismiss.
  // ไทย: NotificationInboxPage — inbox การแจ้งเตือน in-app แบบ persist พร้อม read/dismiss
  NotificationInboxPage: () => import('../../pages/NotificationInboxPage.vue'),

  // English: RobotConnectivityPolicyPage — per-robot offline behavior policy settings.
  //          Operator configures: timeout before action, action (continue/stop/return_to_base/e_stop),
  //          and whether to resume mission on reconnect.
  // ไทย: RobotConnectivityPolicyPage — ตั้งค่า offline behavior policy ต่อ robot
  //      operator ตั้ง: timeout ก่อน action, action (ทำงานต่อ/หยุด/กลับฐาน/E-STOP),
  //      และจะ resume mission เมื่อ reconnect หรือไม่
  RobotConnectivityPolicyPage: () => import('../../pages/RobotConnectivityPolicyPage.vue'),

  // English: GCSConfigPage — tenant GCS bucket setup for video recording storage.
  // ไทย: GCSConfigPage — ตั้งค่า GCS bucket สำหรับเก็บวีดีโอจากกล้องหุ่นยนต์
  GCSConfigPage: () => import('../../pages/GCSConfigPage.vue'),

  // English: VideoViewerPage — live camera stream and video archive playback.
  // ไทย: VideoViewerPage — ดู live camera และวีดีโอย้อนหลัง
  VideoViewerPage: () => import('../../pages/VideoViewerPage.vue'),

  // English: VisionPolicyPage — per-robot AI detection response policy (action per class).
  // ไทย: VisionPolicyPage — ตั้งค่า policy การตอบสนอง AI detection ต่อ robot (action ต่อ class)
  VisionPolicyPage: () => import('../../pages/VisionPolicyPage.vue'),

  // English: WebhookConfigPage — tenant outbound webhook endpoint management.
  //          Create/edit/delete webhook URLs, select event subscriptions, test delivery, view log.
  // ไทย: WebhookConfigPage — จัดการ outbound webhook endpoint ของ tenant
  //      สร้าง/แก้ไข/ลบ URL, เลือก event ที่ subscribe, ทดสอบ delivery, ดู log
  WebhookConfigPage: () => import('../../pages/WebhookConfigPage.vue'),

  // English: VideoRetentionPage — GCS video retention policy configuration.
  //          Supports time-based (delete by age), capacity-based (delete oldest when over limit),
  //          combined mode, or disabled (keep forever).
  // ไทย: VideoRetentionPage — ตั้งค่า retention policy สำหรับวีดีโอบน GCS
  //      รองรับ time-based (ลบตามอายุ), capacity-based (ลบเก่าสุดเมื่อเกิน limit),
  //      combined mode หรือ disabled (เก็บตลอด)
  VideoRetentionPage: () => import('../../pages/VideoRetentionPage.vue'),

  // English: MotorTestPage — Motor control test lab with real-time graph and detection feed.
  //          Select robot, start/stop motor, view motor event graph, monitor person detection.
  // ไทย: MotorTestPage — ห้องทดลอง motor control พร้อม graph realtime และ detection feed
  //      เลือก robot, start/stop motor, ดูกราฟ motor event, monitor person detection
  MotorTestPage: () => import('../../pages/MotorTestPage.vue'),

  // English: LidarTestPage — LiDAR proximity test lab with real-time 8-sector radar visualization.
  //          Select robot, view real-time 8-sector proximity data, monitor collision avoidance.
  // ไทย: LidarTestPage — ห้องทดลอง LiDAR proximity พร้อม 8-sector radar realtime
  //      เลือก robot, ดู 8-sector proximity data realtime, monitor collision avoidance
  LidarTestPage: () => import('../../pages/LidarTestPage.vue'),

  // English: SystemMetricsPage — System health monitoring dashboard with CPU%, RAM%, Disk%, Temperature.
  //          Select robot, view real-time metrics with color-coded alerts and history.
  // ไทย: SystemMetricsPage — หน้าวัด system health พร้อม CPU%, RAM%, Disk%, Temperature
  //      เลือก robot, ดู metrics realtime พร้อม alert color-coded และประวัติ
  SystemMetricsPage: () => import('../../pages/SystemMetricsPage.vue'),

  // English: HistoricalDataPage — Historical data analytics dashboard for querying past system metrics, motor events, vision events.
  //          Select robot and date range, view metrics chart, event tables, and aggregated statistics over time.
  // ไทย: HistoricalDataPage — หน้า analytics ข้อมูลประวัติศาสตร์สำหรับค้นหา system metrics, motor events, vision events ที่ผ่านมา
  //      เลือก robot และช่วงเวลา, ดู metrics chart, event tables และ aggregated statistics
  HistoricalDataPage: () => import('../../pages/HistoricalDataPage.vue'),

  // English: BehaviorLogPage — Real-time robot behavior decision log (Phase 9e).
  //          Shows WHY robot moves, turns, reverses, or stops. Displays event_type, reason, PWM, and context.
  // ไทย: BehaviorLogPage — log การตัดสินใจของหุ่นยนต์แบบ real-time (Phase 9e)
  //      แสดงว่าทำไมหุ่นยนต์วิ่ง เลี้ยว ถอย หรือจอด พร้อม event_type, reason, PWM, context
  BehaviorLogPage: () => import('../../pages/BehaviorLogPage.vue'),
  // English: SlamMapPage — Live SLAM OccupancyGrid viewer (Phase 10).
  //          Select robot, view canvas-rendered map from map_publisher_node via WebSocket 'map' channel.
  // ไทย: SlamMapPage — หน้าดู SLAM OccupancyGrid แบบ live (Phase 10)
  //      เลือก robot แล้วดู map ที่ render บน canvas จาก map_publisher_node ผ่าน WebSocket 'map' channel
  SlamMapPage: () => import('../../pages/SlamMapPage.vue'),
  // English: PersonTrackingPage — Known Persons Registry viewer (Phase 11D).
  //          Browse persons identified by robot camera, view sighting history, edit labels, delete records.
  // ไทย: PersonTrackingPage — หน้าดู Known Persons Registry (Phase 11D)
  //      ดูบุคคลที่กล้อง robot ระบุตัวตน, ดูประวัติการพบเห็น, แก้ label, ลบ
  PersonTrackingPage: () => import('../../pages/PersonTrackingPage.vue'),

  // English: RobotOperationPage — Single-robot operation view with GPS Leaflet map (70%) + LiDAR proximity radar (30%) + status bar.
  //          Navigate via /dashboard/robot-view/:robotId. Shows live GPS marker, 8-sector LiDAR proximity, mission progress and status.
  // ไทย: RobotOperationPage — หน้าดู robot 1 ตัว: GPS Leaflet map (70%) + LiDAR proximity radar (30%) + status bar
  //      เข้าผ่าน /dashboard/robot-view/:robotId แสดง GPS marker realtime, LiDAR 8-sector, mission progress และ status
  RobotOperationPage: () => import('../../pages/RobotOperationPage.vue'),
};

function buildLazyPage(registryKey, debugLabel) {
  return async () => {
    // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
    // emitDashboardRouteDebug(
    //   `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_START] English: starting lazy import for dashboard route component | ไทย: เริ่ม lazy import สำหรับ component ของ dashboard route`,
    //   {
    //     registryKey,
    //     debugLabel,
    //   },
    // );

    try {
      const loader = PAGE_REGISTRY[registryKey];

      if (!loader) {
        throw new Error(
          `${ROUTE_DEBUG_NAMESPACE} No registry entry for key: ${registryKey}. Add a static import() entry to PAGE_REGISTRY.`,
        );
      }

      const module = await loader();

      // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
      // emitDashboardRouteDebug(
      //   `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_SUCCESS] English: dashboard route component loaded successfully | ไทย: โหลด component ของ dashboard route สำเร็จ`,
      //   {
      //     registryKey,
      //     debugLabel,
      //     exportKeys: Object.keys(module || {}),
      //     hasDefaultExport: Boolean(module && module.default),
      //   },
      // );

      return module;
    } catch (error) {
      console.error(
        `${ROUTE_DEBUG_NAMESPACE} [LAZY_IMPORT_ERROR] English: failed to load dashboard route component | ไทย: โหลด component ของ dashboard route ไม่สำเร็จ`,
        {
          registryKey,
          debugLabel,
          error: error instanceof Error ? error.message : String(error),
        },
      );
      throw error;
    }
  };
}

function createDashboardMeta(meta = {}) {
  const merged = {
    section: 'dashboard',
    requiresAuth: true,
    requiresAdmin: false,
    layout: 'dashboard',
    navGroup: 'dashboard',
    debug_en: 'Dashboard route metadata helps validate whether fleet monitoring pages are grouped correctly and remain separate from admin-only routes.',
    debug_th: 'metadata ของ dashboard route ใช้ตรวจว่าหน้าที่ใช้มอนิเตอร์ fleet ถูกจัดกลุ่มอย่างถูกต้องและแยกจาก route ที่เป็น admin-only หรือไม่',
    ...meta,
  };

  // [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
  // emitDashboardRouteDebug(
  //   `${ROUTE_DEBUG_NAMESPACE} [META_CREATED] English: created dashboard route metadata | ไทย: สร้าง metadata ของ dashboard route แล้ว`,
  //   merged,
  // );

  return merged;
}

const dashboardRoutes = [
  {
    path: '/',
    redirect: '/dashboard',
    meta: createDashboardMeta({
      title: 'Root Redirect',
      navLabel: 'Root',
      routePurpose_en: 'Root route redirects into the primary dashboard page.',
      routePurpose_th: 'route หลักของระบบจะ redirect ไปยังหน้า dashboard หลัก',
    }),
  },
  {
    path: '/dashboard',
    name: 'DashboardHome',
    component: buildLazyPage('DashboardPageTest', 'DashboardPageTest'),
    meta: createDashboardMeta({
      title: 'Fleet Dashboard',
      navLabel: 'Dashboard',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Temporary lightweight dashboard test page used to validate production-shell routing before re-enabling the full realtime dashboard.',
      routePurpose_th: 'หน้า dashboard ทดสอบแบบเบาชั่วคราว ใช้ตรวจ production-shell routing ก่อนเปิดใช้ realtime dashboard เต็มรูปแบบอีกครั้ง',
    }),
  },
  {
    path: '/dashboard/live',
    name: 'DashboardLive',
    component: buildLazyPage('FleetDashboardPage', 'FleetDashboardPageLive'),
    meta: createDashboardMeta({
      title: 'Fleet Operations',
      navLabel: 'Live',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Fleet operations page — realtime robot map, status panel, mission and alert summaries.',
      routePurpose_th: 'หน้า fleet operations — แผนที่หุ่นยนต์ realtime, status panel, สรุป mission และ alert',
    }),
  },
  {
    path: '/dashboard/channels',
    name: 'RobotChannelExplorer',
    component: buildLazyPage('RobotChannelExplorerPage', 'RobotChannelExplorerPage'),
    meta: createDashboardMeta({
      title: 'Robot Channel Explorer',
      navLabel: 'Channels',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Per-channel realtime data explorer for all 8 channels: telemetry, status, mission, alert, ack, command, map, factsheet.',
      routePurpose_th: 'หน้า explorer ข้อมูล realtime แยกตาม channel ครบ 8 channels: telemetry, status, mission, alert, ack, command, map, factsheet',
    }),
  },
  {
    path: '/dashboard/command',
    name: 'MobileCommand',
    component: buildLazyPage('MobileCommandPage', 'MobileCommandPage'),
    meta: createDashboardMeta({
      title: 'Robot Command',
      navLabel: 'Command',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Mobile-first command interface — E-STOP, motion, mission commands, real-time ACK.',
      routePurpose_th: 'หน้า command แบบ mobile-first — E-STOP, คำสั่ง motion, mission, แสดง ACK แบบ real-time',
    }),
  },
  {
    path: '/dashboard/map-builder',
    name: 'MapBuilder',
    component: buildLazyPage('MapBuilderPage', 'MapBuilderPage'),
    meta: createDashboardMeta({
      title: 'Map Builder',
      navLabel: 'Map Builder',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'SLAM map viewer and zone builder — scan area, draw zone polygon, generate waypoints, create mission from zone.',
      routePurpose_th: 'หน้าดูแผนที่ SLAM และวาด zone — สแกนพื้นที่, วาด zone polygon, สร้าง waypoints, สร้าง mission จาก zone',
    }),
  },
  {
    path: '/dashboard/calibration',
    name: 'RobotCalibration',
    component: buildLazyPage('RobotCalibrationPage', 'RobotCalibrationPage'),
    meta: createDashboardMeta({
      title: 'Steering Calibration',
      navLabel: 'Calibration',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Mechanical calibration — configure Ackermann steering params per robot and monitor live steering error vs command.',
      routePurpose_th: 'Calibrate ทางกล — ตั้ง Ackermann steering params ต่อ robot และดู steering error vs command แบบ live',
    }),
  },
  {
    path: '/dashboard/calibration/blade',
    name: 'RobotBladeCalibration',
    component: buildLazyPage('RobotBladeCalibrationPage', 'RobotBladeCalibrationPage'),
    meta: createDashboardMeta({
      title: 'Blade Calibration',
      navLabel: 'Blade',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Blade motor calibration for MOWING robots — set target RPM, overcurrent threshold, and start delay.',
      routePurpose_th: 'Calibrate blade motor สำหรับ MOWING robot — ตั้ง target RPM, threshold กระแสเกิน, delay ก่อนเริ่ม',
    }),
  },
  {
    path: '/dashboard/calibration/nav',
    name: 'RobotNavCalibration',
    component: buildLazyPage('RobotNavCalibrationPage', 'RobotNavCalibrationPage'),
    meta: createDashboardMeta({
      title: 'Navigation Calibration',
      navLabel: 'Navigation',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Navigation speed profile and obstacle detection calibration for all robot types.',
      routePurpose_th: 'ตั้งค่า speed profile และ obstacle detection สำหรับ robot ทุกชนิด',
    }),
  },
  {
    path: '/dashboard/calibration/scrub',
    name: 'RobotScrubCalibration',
    component: buildLazyPage('RobotScrubCalibrationPage', 'RobotScrubCalibrationPage'),
    meta: createDashboardMeta({
      title: 'Scrubbing Calibration',
      navLabel: 'Scrubbing',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Pump flow and brush RPM calibration for SCRUBBING robots — configure water delivery and brush pressure.',
      routePurpose_th: 'Calibrate pump flow และ brush RPM สำหรับ SCRUBBING robot — ตั้งค่าการส่งน้ำและแรงกดแปรง',
    }),
  },
  {
    path: '/dashboard/thresholds',
    name: 'RobotThresholds',
    component: buildLazyPage('RobotThresholdsPage', 'RobotThresholdsPage'),
    meta: createDashboardMeta({
      title: 'Robot Thresholds',
      navLabel: 'Thresholds',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Operator settings — configure per-robot thresholds for battery and scrubbing tank alerts, then push to robot-edge via MQTT.',
      routePurpose_th: 'ตั้งค่า threshold ต่อ robot สำหรับ battery และถังน้ำ scrubbing แล้ว push ไปยัง robot-edge ผ่าน MQTT',
    }),
  },
  {
    path: '/dashboard/analytics',
    name: 'FleetAnalytics',
    component: buildLazyPage('FleetAnalyticsPage', 'FleetAnalyticsPage'),
    meta: createDashboardMeta({
      title: 'Fleet Analytics',
      navLabel: 'Analytics',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Fleet KPI dashboard — utilization %, coverage area/day, MTBF, battery health, per-robot breakdown.',
      routePurpose_th: 'Dashboard KPI fleet — utilization %, พื้นที่/วัน, MTBF, battery health, แยกตาม robot',
    }),
  },
  {
    path: '/dashboard/schedules',
    name: 'ScheduledMissions',
    component: buildLazyPage('ScheduledMissionsPage', 'ScheduledMissionsPage'),
    meta: createDashboardMeta({
      title: 'Scheduled Missions',
      navLabel: 'Schedules',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Recurring mission schedule manager — create, edit, activate/deactivate cron and interval schedules.',
      routePurpose_th: 'จัดการ scheduled mission — สร้าง, แก้ไข, เปิด/ปิด cron และ interval schedule',
    }),
  },
  {
    path: '/dashboard/maintenance',
    name: 'RobotMaintenanceLog',
    component: buildLazyPage('RobotMaintenanceLogPage', 'RobotMaintenanceLogPage'),
    meta: createDashboardMeta({
      title: 'Maintenance Log',
      navLabel: 'Maintenance',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Robot service history — log and browse maintenance events per robot (preventive, corrective, inspection).',
      routePurpose_th: 'ประวัติการซ่อมบำรุง robot — บันทึกและดู maintenance event ต่อ robot',
    }),
  },
  {
    path: '/dashboard/notifications',
    name: 'NotificationInbox',
    component: buildLazyPage('NotificationInboxPage', 'NotificationInboxPage'),
    meta: createDashboardMeta({
      title: 'Notifications',
      navLabel: 'Notifications',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Persistent in-app notification inbox — filter by type, mark as read, dismiss.',
      routePurpose_th: 'Inbox การแจ้งเตือน in-app แบบ persist — filter ตาม type, mark read, dismiss',
    }),
  },
  {
    path: '/dashboard/gcs-config',
    name: 'GCSConfig',
    component: buildLazyPage('GCSConfigPage', 'GCSConfigPage'),
    meta: createDashboardMeta({
      title: 'Video Storage (GCS)',
      navLabel: 'GCS Config',
      permissions: ['platform_admin', 'tenant_admin'],
      routePurpose_en: 'Tenant GCS bucket configuration — enter bucket name, project ID, and service account credentials for video recording storage.',
      routePurpose_th: 'ตั้งค่า GCS bucket ของ tenant — กรอก bucket name, project ID และ service account credentials สำหรับเก็บวีดีโอ',
    }),
  },
  {
    path: '/dashboard/video',
    name: 'VideoViewer',
    component: buildLazyPage('VideoViewerPage', 'VideoViewerPage'),
    meta: createDashboardMeta({
      title: 'Video Viewer',
      navLabel: 'Video',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Live camera stream viewer and video archive playback with detection filtering.',
      routePurpose_th: 'ดู live camera และวีดีโอย้อนหลัง พร้อม filter ตาม detection class',
    }),
  },
  {
    path: '/dashboard/vision-policy',
    name: 'VisionPolicy',
    component: buildLazyPage('VisionPolicyPage', 'VisionPolicyPage'),
    meta: createDashboardMeta({
      title: 'Vision Policy',
      navLabel: 'Vision Policy',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Per-robot AI detection response policy — configure action (ignore/slow/stop/e_stop), confidence threshold, and resume delay per object class.',
      routePurpose_th: 'policy การตอบสนอง AI detection ต่อ robot — ตั้ง action, confidence threshold และ resume delay ต่อ class',
    }),
  },
  {
    path: '/dashboard/connectivity-policy',
    name: 'RobotConnectivityPolicy',
    component: buildLazyPage('RobotConnectivityPolicyPage', 'RobotConnectivityPolicyPage'),
    meta: createDashboardMeta({
      title: 'Connectivity Policy',
      navLabel: 'Connectivity',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Per-robot offline behavior policy — configure how long to wait before triggering an action when MQTT signal is lost, and what action to take (continue, stop, return to base, emergency stop).',
      routePurpose_th: 'ตั้งค่า policy การทำงานเมื่อ offline ต่อ robot — กำหนดว่าจะรอกี่วินาทีก่อน trigger action เมื่อ MQTT ขาด และจะทำอะไร (ทำงานต่อ, หยุด, กลับฐาน, E-STOP)',
    }),
  },
  {
    path: '/dashboard/webhooks',
    name: 'WebhookConfig',
    component: buildLazyPage('WebhookConfigPage', 'WebhookConfigPage'),
    meta: createDashboardMeta({
      title: 'Webhook Endpoints',
      navLabel: 'Webhooks',
      permissions: ['platform_admin', 'tenant_admin'],
      routePurpose_en: 'Outbound webhook endpoint management — register URLs, select event types, test delivery, view delivery log.',
      routePurpose_th: 'จัดการ outbound webhook endpoint — ลงทะเบียน URL, เลือก event type, ทดสอบ delivery, ดู delivery log',
    }),
  },
  {
    path: '/dashboard/video-retention',
    name: 'VideoRetention',
    component: buildLazyPage('VideoRetentionPage', 'VideoRetentionPage'),
    meta: createDashboardMeta({
      title: 'Video Retention',
      navLabel: 'Retention',
      permissions: ['platform_admin', 'tenant_admin'],
      routePurpose_en: 'GCS video retention policy — configure time-based deletion (by age), capacity-based deletion (delete oldest when over limit), combined mode, or disabled.',
      routePurpose_th: 'policy retention วีดีโอบน GCS — ตั้งค่าลบตามอายุ, ลบเก่าสุดเมื่อเกิน capacity, combined mode หรือปิดการลบ (เก็บตลอด)',
    }),
  },
  {
    path: '/dashboard/diagnostics',
    name: 'DashboardDiagnostics',
    component: buildLazyPage('DashboardPageTest', 'DashboardPageTestDiagnosticsAlias'),
    meta: createDashboardMeta({
      title: 'Dashboard Diagnostics',
      navLabel: 'Diagnostics',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Temporary diagnostics route reusing the lightweight dashboard test page while the full realtime dashboard remains isolated.',
      routePurpose_th: 'route diagnostics ชั่วคราวที่ใช้หน้า dashboard ทดสอบแบบเบาซ้ำ ระหว่างที่แยก full realtime dashboard ออกไปก่อน',
      aliasOf: 'DashboardHome',
    }),
  },
  {
    path: '/dashboard/motor-test',
    name: 'MotorTest',
    component: buildLazyPage('MotorTestPage', 'MotorTestPage'),
    meta: createDashboardMeta({
      title: 'Motor Test Lab',
      navLabel: 'Motor Test',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Motor control test lab — select robot, start/stop motor, view real-time motor event graph and person detection feed.',
      routePurpose_th: 'ห้องทดลอง motor control — เลือก robot, start/stop motor, ดู motor event graph และ person detection feed realtime',
    }),
  },
  {
    path: '/dashboard/lidar-test',
    name: 'LidarTest',
    component: buildLazyPage('LidarTestPage', 'LidarTestPage'),
    meta: createDashboardMeta({
      title: 'LiDAR Test Lab',
      navLabel: 'LiDAR Test',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'LiDAR proximity test lab — select robot, view real-time 8-sector proximity radar and distance measurements.',
      routePurpose_th: 'ห้องทดลอง LiDAR proximity — เลือก robot, ดู 8-sector proximity radar และระยะทาง realtime',
    }),
  },
  {
    path: '/dashboard/system-metrics',
    name: 'SystemMetrics',
    component: buildLazyPage('SystemMetricsPage', 'SystemMetricsPage'),
    meta: createDashboardMeta({
      title: 'System Metrics',
      navLabel: 'System Metrics',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'System health monitoring — select robot, view real-time CPU%, RAM%, Disk%, Temperature with alerts.',
      routePurpose_th: 'การวัด system health — เลือก robot, ดู CPU%, RAM%, Disk%, Temperature realtime พร้อม alerts',
    }),
  },
  {
    path: '/dashboard/historical',
    name: 'HistoricalData',
    component: buildLazyPage('HistoricalDataPage', 'HistoricalDataPage'),
    meta: createDashboardMeta({
      title: 'Historical Data',
      navLabel: 'Historical',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Historical data analytics — query system metrics, motor events, vision events by date range with filtering and aggregated statistics.',
      routePurpose_th: 'Analytics ข้อมูลประวัติศาสตร์ — ค้นหา system metrics, motor events, vision events ตามช่วงเวลา พร้อม filtering และ aggregated statistics',
    }),
  },
  {
    path: '/dashboard/behavior-log',
    name: 'BehaviorLog',
    component: buildLazyPage('BehaviorLogPage', 'BehaviorLogPage'),
    meta: createDashboardMeta({
      title: 'Behavior Log',
      navLabel: 'Behavior Log',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Robot behavior decision log (Phase 9e) — select robot, view real-time WHY the robot moves, turns, stops, or reverses with event_type, reason, and context.',
      routePurpose_th: 'Log การตัดสินใจของหุ่นยนต์ (Phase 9e) — เลือก robot, ดูว่าทำไมหุ่นยนต์วิ่ง เลี้ยว จอด หรือถอย พร้อม event_type, reason และ context',
    }),
  },
  {
    path: '/dashboard/slam-map',
    name: 'SlamMap',
    component: buildLazyPage('SlamMapPage', 'SlamMapPage'),
    meta: createDashboardMeta({
      title: 'SLAM Map',
      navLabel: 'SLAM Map',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Live SLAM OccupancyGrid viewer (Phase 10) — select robot, view canvas-rendered map from map_publisher_node updated every ~10s.',
      routePurpose_th: 'หน้าดู SLAM OccupancyGrid แบบ live (Phase 10) — เลือก robot แล้วดู map ที่ render บน canvas จาก map_publisher_node อัปเดตทุก ~10s',
    }),
  },
  {
    path: '/dashboard/persons',
    name: 'PersonTracking',
    component: buildLazyPage('PersonTrackingPage', 'PersonTrackingPage'),
    meta: createDashboardMeta({
      title: 'Known Persons',
      navLabel: 'Known Persons',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator'],
      routePurpose_en: 'Known Persons Registry (Phase 11D) — browse persons identified by robot camera, view sighting history, edit labels, delete records.',
      routePurpose_th: 'Known Persons Registry (Phase 11D) — ดูบุคคลที่กล้อง robot ระบุตัวตน, ดูประวัติการพบเห็น, แก้ label, ลบ',
    }),
  },
  {
    path: '/dashboard/robot-view/:robotId',
    name: 'RobotOperationView',
    component: buildLazyPage('RobotOperationPage', 'RobotOperationPage'),
    meta: createDashboardMeta({
      title: 'Robot Operation View',
      navLabel: 'Robot View',
      permissions: ['platform_admin', 'tenant_admin', 'site_operator', 'viewer'],
      routePurpose_en: 'Single-robot operation view — GPS Leaflet map with live marker (left 70%), LiDAR 8-sector proximity radar (right 30%), status bar (bottom). Navigate via /dashboard/robot-view/:robotId from Fleet Live.',
      routePurpose_th: 'หน้าดู robot 1 ตัว — GPS Leaflet map พร้อม marker realtime (ซ้าย 70%), LiDAR 8-sector proximity radar (ขวา 30%), status bar (ล่าง) เข้าผ่าน /dashboard/robot-view/:robotId จากหน้า Fleet Live',
    }),
  },
];

// [Debug output disabled — freeze/performance isolation | ปิด debug output ชั่วคราวเพื่อแยกปัญหา freeze/performance]
// emitDashboardRouteDebug(
//   `${ROUTE_DEBUG_NAMESPACE} [ROUTES_REGISTERED] English: dashboard route module initialized | ไทย: module ของ dashboard routes ถูกเตรียมใช้งานแล้ว`,
//   {
//     routeCount: dashboardRoutes.length,
//     routeNames: dashboardRoutes.map((route) => route.name || `redirect:${route.path}`),
//     paths: dashboardRoutes.map((route) => route.path),
//     registryKeys: Object.keys(PAGE_REGISTRY),
//     explanation_en: 'Use this snapshot to verify that dashboard routes are grouped separately from admin, robot, mission, and alert route modules.',
//     explanation_th: 'ใช้ snapshot นี้ตรวจว่า dashboard routes ถูกแยกออกจาก admin, robot, mission และ alert route modules อย่างถูกต้อง',
//   },
// );

export default dashboardRoutes;
